import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadCloud, Loader2, Search, X, Plus, FileDown, Sparkles, Database, SlidersHorizontal, Bookmark, CalendarClock, PlayCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SYSTEM_LABELS } from "@/lib/connectionConfig";
import { SfOrgSwitcher } from "@/components/SfOrgSwitcher";

const FORMATS = [
  { id: "csv", label: "CSV" },
  { id: "xlsx", label: "Excel (XLSX)" },
  { id: "json", label: "JSON" },
];
const SPLIT_MODES = [
  { id: "none", label: "Single file" },
  { id: "records", label: "By record count" },
  { id: "size", label: "By file size (MB)" },
];
const COMBINE_MODES = [
  { id: "separate", label: "Separate files (one per object)" },
  { id: "stacked", label: "Single consolidated file (stacked)" },
  { id: "joined", label: "Single joined file (related)" },
];
const SOQL_TEMPLATES = [
  { label: "Recent Accounts", query: "SELECT Id, Name, Industry, CreatedDate FROM Account ORDER BY CreatedDate DESC" },
  { label: "Open Opportunities", query: "SELECT Id, Name, StageName, Amount, CloseDate, Account.Name FROM Opportunity WHERE IsClosed = false" },
  { label: "Closed-Won this quarter", query: "SELECT Id, Name, Amount, CloseDate, Account.Name FROM Opportunity WHERE StageName = 'Closed Won' AND CloseDate = THIS_QUARTER" },
  { label: "Contacts with Account name", query: "SELECT Id, FirstName, LastName, Email, Account.Name FROM Contact" },
  { label: "Active Users", query: "SELECT Id, Name, Email, Profile.Name, IsActive FROM User WHERE IsActive = true" },
  { label: "Cases created last 7 days", query: "SELECT Id, CaseNumber, Subject, Status, Priority, CreatedDate FROM Case WHERE CreatedDate = LAST_N_DAYS:7" },
];
const MAX_OBJECTS = 20;

function fmtBytes(b) {
  if (!b) return "0 KB";
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(2)} MB`;
  return `${(b / 1024).toFixed(1)} KB`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function Extraction() {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const [systems, setSystems] = useState([]);
  const [system, setSystem] = useState("");
  const [objects, setObjects] = useState([]);
  const [objSource, setObjSource] = useState("");
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [selected, setSelected] = useState([]);
  const [recs, setRecs] = useState([]);
  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("csv");
  const [splitMode, setSplitMode] = useState("none");
  const [splitValue, setSplitValue] = useState(1000);
  const [rowLimit, setRowLimit] = useState(1000);
  const [combine, setCombine] = useState("separate");
  const [queryMode, setQueryMode] = useState("objects"); // objects | soql (Salesforce only)
  const [soql, setSoql] = useState("");
  // Saved SOQL templates
  const [savedSoql, setSavedSoql] = useState([]);
  const [saveSoqlOpen, setSaveSoqlOpen] = useState(false);
  const [soqlName, setSoqlName] = useState("");
  const [savingSoql, setSavingSoql] = useState(false);
  const [sfRefresh, setSfRefresh] = useState(0);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState("");
  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(null);
  // Presets
  const [presets, setPresets] = useState([]);
  const [pendingPreset, setPendingPreset] = useState(null);
  const [savePresetOpen, setSavePresetOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleFreq, setScheduleFreq] = useState("daily");
  const [savingSchedule, setSavingSchedule] = useState(false);
  // Field picker
  const [fieldsByObject, setFieldsByObject] = useState({});
  const [fieldDialogObj, setFieldDialogObj] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [workingFields, setWorkingFields] = useState([]);
  const [fieldSearch, setFieldSearch] = useState("");

  useEffect(() => {
    api.get("/connections").then((r) => {
      const connected = r.data.filter((c) => c.connected);
      setSystems(connected);
      if (connected[0]) setSystem(connected[0].system);
    });
    loadHistory();
    loadPresets();
    loadSavedSoql();
  }, []);

  const loadHistory = () => api.get("/extraction/history").then((r) => setHistory(r.data)).catch(() => {});
  const loadPresets = () => api.get("/extraction-presets").then((r) => setPresets(r.data)).catch(() => {});
  const loadSavedSoql = () => api.get("/soql-templates").then((r) => setSavedSoql(r.data)).catch(() => {});

  useEffect(() => {
    if (!system) return;
    setSelected([]);
    setRecs([]);
    setObjects([]);
    if (system !== "salesforce") setQueryMode("objects");
    setLoadingObjects(true);
    api
      .get(`/extraction/objects?system=${system}`)
      .then((r) => {
        setObjects(r.data.objects || []);
        setObjSource(r.data.source);
        if (r.data.source === "error") toast.error(`Couldn't load objects: ${r.data.error}`);
        if (pendingPreset && pendingPreset.system === system) {
          applyPreset(pendingPreset);
          setPendingPreset(null);
        }
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load objects"))
      .finally(() => setLoadingObjects(false));
  }, [system, sfRefresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? objects.filter((o) => o.name.toLowerCase().includes(q) || (o.label || "").toLowerCase().includes(q)) : objects;
    return list.slice(0, 200);
  }, [objects, search]);

  const toggle = (name) => {
    if (selected.includes(name)) {
      setSelected((s) => s.filter((x) => x !== name));
      setFieldsByObject((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      return;
    }
    if (selected.length >= MAX_OBJECTS) {
      toast.error(`You can select up to ${MAX_OBJECTS} objects`);
      return;
    }
    setSelected((s) => [...s, name]);
    fetchRecs(name);
  };

  const fetchRecs = (name) => {
    api
      .get(`/extraction/recommendations?system=${system}&object=${encodeURIComponent(name)}`)
      .then((r) => {
        const fresh = (r.data.recommendations || []).filter((x) => !selected.includes(x.name) && x.name !== name);
        setRecs(fresh);
      })
      .catch(() => setRecs([]));
  };

  const addRec = (name) => {
    if (selected.length >= MAX_OBJECTS) {
      toast.error(`You can select up to ${MAX_OBJECTS} objects`);
      return;
    }
    setSelected((s) => (s.includes(name) ? s : [...s, name]));
    setRecs((r) => r.filter((x) => x.name !== name));
  };

  const setMode = (m) => {
    setSplitMode(m);
    if (m === "records") setSplitValue(1000);
    if (m === "size") setSplitValue(1);
  };

  const applyPreset = (p) => {
    setSelected(p.objects || []);
    setFieldsByObject(p.fields || {});
    setFormat(p.format || "csv");
    setSplitMode(p.split_mode || "none");
    setSplitValue(p.split_value || (p.split_mode === "size" ? 1 : 1000));
    setRowLimit(p.row_limit || 1000);
    setCombine(p.combine || "separate");
    setQueryMode("objects");
    setRecs([]);
  };

  const loadPreset = (p) => {
    if (p.system === system) {
      applyPreset(p);
    } else {
      setPendingPreset(p);
      setSystem(p.system);
    }
    toast.success(`Loaded preset “${p.name}”`);
  };

  const savePreset = async () => {
    if (!presetName.trim()) {
      toast.error("Name your preset");
      return;
    }
    if (!selected.length) {
      toast.error("Select at least one object first");
      return;
    }
    const cleanFields = {};
    Object.entries(fieldsByObject).forEach(([o, c]) => {
      if (c && c.length) cleanFields[o] = c;
    });
    setSavingPreset(true);
    try {
      const { data } = await api.post("/extraction-presets", {
        name: presetName.trim(),
        system,
        objects: selected,
        fields: cleanFields,
        format,
        split_mode: splitMode,
        split_value: splitMode === "none" ? 0 : Number(splitValue),
        row_limit: Math.max(1, Math.min(Number(rowLimit) || 1000, 50000)),
        combine,
      });
      setPresets((ps) => [data, ...ps]);
      setSavePresetOpen(false);
      setPresetName("");
      toast.success("Preset saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save preset");
    } finally {
      setSavingPreset(false);
    }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/extraction-presets/${id}`);
      setPresets((ps) => ps.filter((p) => p.id !== id));
      toast.success("Preset deleted");
    } catch {
      toast.error("Failed to delete preset");
    }
  };

  const openSchedule = (p) => {
    setScheduleTarget(p);
    setScheduleFreq(p.schedule_enabled ? p.schedule : "daily");
  };

  const saveSchedule = async (frequency) => {
    if (!scheduleTarget) return;
    setSavingSchedule(true);
    try {
      const { data } = await api.post(`/extraction-presets/${scheduleTarget.id}/schedule`, {
        frequency,
        enabled: frequency !== "none",
      });
      setPresets((ps) => ps.map((p) => (p.id === data.id ? data : p)));
      toast.success(
        frequency === "none"
          ? "Schedule turned off"
          : `Scheduled ${frequency} — a download link will be emailed to ${user?.email}`
      );
      setScheduleTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const runNow = async (p) => {
    try {
      const { data } = await api.post(`/extraction-presets/${p.id}/run-now`);
      toast.success(data.message || "Export started");
      setTimeout(loadPresets, 6000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to run export");
    }
  };

  const scheduled = presets.filter((p) => p.schedule_enabled);

  const saveSoqlTemplate = async () => {
    if (!soqlName.trim()) {
      toast.error("Name your query");
      return;
    }
    if (!soql.trim().toLowerCase().startsWith("select")) {
      toast.error("Enter a valid SOQL query starting with SELECT");
      return;
    }
    setSavingSoql(true);
    try {
      const { data } = await api.post("/soql-templates", { name: soqlName.trim(), query: soql.trim() });
      setSavedSoql((s) => [data, ...s]);
      setSaveSoqlOpen(false);
      setSoqlName("");
      toast.success("Query saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save query");
    } finally {
      setSavingSoql(false);
    }
  };

  const deleteSoqlTemplate = async (id) => {
    try {
      await api.delete(`/soql-templates/${id}`);
      setSavedSoql((s) => s.filter((t) => t.id !== id));
      toast.success("Query deleted");
    } catch {
      toast.error("Failed to delete query");
    }
  };

  const openFieldPicker = (obj) => {
    setFieldDialogObj(obj);
    setFieldSearch("");
    setAvailableFields([]);
    setWorkingFields([]);
    setLoadingFields(true);
    api
      .get(`/extraction/fields?system=${system}&object=${encodeURIComponent(obj)}`)
      .then((r) => {
        const fields = r.data.fields || [];
        setAvailableFields(fields);
        const existing = fieldsByObject[obj];
        setWorkingFields(existing && existing.length ? existing : fields.map((f) => f.name));
      })
      .catch(() => toast.error("Failed to load fields for this object"))
      .finally(() => setLoadingFields(false));
  };

  const toggleWorkingField = (name) => {
    setWorkingFields((cur) => (cur.includes(name) ? cur.filter((x) => x !== name) : [...cur, name]));
  };

  const applyFields = () => {
    const allNames = availableFields.map((f) => f.name);
    const isAll = workingFields.length === allNames.length;
    setFieldsByObject((prev) => ({ ...prev, [fieldDialogObj]: isAll ? [] : workingFields }));
    setFieldDialogObj(null);
  };

  const filteredFields = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    if (!q) return availableFields;
    return availableFields.filter((f) => f.name.toLowerCase().includes(q) || (f.label || "").toLowerCase().includes(q));
  }, [availableFields, fieldSearch]);

  const pollExtraction = async (id) => {
    for (let i = 0; i < 200; i++) {
      const { data } = await api.get(`/extraction/${id}`);
      if (data.status === "ready") return data;
      if (data.status === "error") throw new Error(data.error || "Extraction failed");
      await new Promise((res) => setTimeout(res, 1500));
    }
    throw new Error("Extraction is taking too long — check Recent extractions shortly.");
  };

  const run = async () => {
    const soqlMode = queryMode === "soql" && system === "salesforce";
    if (soqlMode) {
      if (!soql.trim().toLowerCase().startsWith("select")) {
        toast.error("Enter a valid SOQL query starting with SELECT");
        return;
      }
    } else if (!selected.length) {
      toast.error("Select at least one object");
      return;
    }
    if (splitMode !== "none" && (!splitValue || splitValue <= 0)) {
      toast.error("Enter a positive split value");
      return;
    }
    // 0 = unlimited (Salesforce object CSV via Bulk API); otherwise clamp.
    const n = Number(rowLimit);
    const largeAllowed = system === "salesforce" && queryMode === "objects" && format === "csv" && combine === "separate";
    const limit = n === 0 && largeAllowed ? 0 : Math.max(1, Math.min(n || 1000, largeAllowed ? 20000000 : 50000));
    // Only send objects that have an explicit non-empty column selection
    const cleanFields = {};
    Object.entries(fieldsByObject).forEach(([obj, cols]) => {
      if (cols && cols.length) cleanFields[obj] = cols;
    });
    setRunning(true);
    setRunStatus("Starting extraction…");
    try {
      const { data: started } = await api.post("/extraction/run", {
        system,
        objects: soqlMode ? [] : selected,
        format,
        split_mode: splitMode,
        split_value: splitMode === "none" ? 0 : Number(splitValue),
        row_limit: limit,
        fields: soqlMode ? {} : cleanFields,
        mode: soqlMode ? "soql" : "objects",
        soql: soqlMode ? soql.trim() : "",
        combine: soqlMode ? "separate" : combine,
      });
      await loadHistory();
      setRunStatus("Extracting records…");
      const data = await pollExtraction(started.id);
      toast.success(
        `Extracted ${(data.total_rows || 0).toLocaleString()} rows · ${data.file_count} file${data.file_count === 1 ? "" : "s"} · ${fmtBytes(data.total_size_bytes)}`
      );
      await loadHistory();
      await downloadFile(data.id, data.filename);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail ? formatApiErrorDetail(detail) : (e.message || "Extraction failed"));
      await loadHistory();
    } finally {
      setRunning(false);
      setRunStatus("");
    }
  };

  const downloadFile = async (id, filename) => {
    setDownloading(id);
    try {
      const res = await api.get(`/extraction/${id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed — the file may have expired. Re-run the extraction.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid="extraction-page">
      <div className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Extraction</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight lg:text-4xl">Data extraction</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Export records from any connected system to CSV, Excel or JSON. Pick up to {MAX_OBJECTS} objects — related
          records keep their IDs so files can be re-imported.
        </p>
      </div>

      {presets.length > 0 && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="preset-bar">
          <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
            <Bookmark className="h-3.5 w-3.5" /> Saved presets
          </div>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-3 pr-1 text-sm transition-colors hover:border-primary/40"
                data-testid={`preset-${p.id}`}
              >
                <button
                  onClick={() => loadPreset(p)}
                  className="font-medium hover:text-primary"
                  data-testid={`preset-load-${p.id}`}
                  title="Load this preset"
                >
                  {p.name}
                </button>
                <span className="text-xs text-muted-foreground">
                  {SYSTEM_LABELS[p.system] || p.system_name} · {p.objects.length} obj
                </span>
                {p.schedule_enabled && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary" data-testid={`preset-schedule-badge-${p.id}`}>
                    <CalendarClock className="h-3 w-3" /> {p.schedule}
                  </span>
                )}
                <button
                  onClick={() => openSchedule(p)}
                  disabled={restricted}
                  className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-primary disabled:opacity-50"
                  data-testid={`preset-schedule-${p.id}`}
                  aria-label={`Schedule preset ${p.name}`}
                  title="Schedule this export"
                >
                  <CalendarClock className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => deletePreset(p.id)}
                  disabled={restricted}
                  className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:opacity-50"
                  data-testid={`preset-delete-${p.id}`}
                  aria-label={`Delete preset ${p.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {systems.length === 0 ? (
        <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border/60 bg-card/40 p-10 text-center">
          <Database className="h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-display text-lg font-bold">No connected systems</p>
          <p className="mt-1 text-sm text-muted-foreground">Connect Salesforce, Zoho or Oracle first — then come back here to export objects.</p>
          <Link
            to="/dashboard/connections"
            className="mt-5 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
            data-testid="extract-empty-connect"
          >
            Connect a system
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* Left: pickers */}
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>System</Label>
                <Select value={system} onValueChange={setSystem}>
                  <SelectTrigger data-testid="extract-system-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {systems.map((s) => (
                      <SelectItem key={s.system} value={s.system} data-testid={`extract-system-${s.system}`}>
                        {SYSTEM_LABELS[s.system] || s.system_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="extract-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FORMATS.map((f) => (
                      <SelectItem key={f.id} value={f.id} data-testid={`extract-format-${f.id}`}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {system === "salesforce" && (
              <SfOrgSwitcher onChanged={() => setSfRefresh((k) => k + 1)} className="rounded-lg border border-border bg-card px-3 py-2" />
            )}

            {system === "salesforce" && (
              <div className="space-y-2" data-testid="extract-query-mode">
                <Label>Query mode</Label>
                <div className="inline-flex rounded-lg border border-border bg-card p-1">
                  <button
                    type="button"
                    onClick={() => setQueryMode("objects")}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${queryMode === "objects" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="extract-mode-objects"
                  >
                    Object picker
                  </button>
                  <button
                    type="button"
                    onClick={() => setQueryMode("soql")}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${queryMode === "soql" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    data-testid="extract-mode-soql"
                  >
                    Custom SOQL
                  </button>
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Split into files</Label>
                <Select value={splitMode} onValueChange={setMode}>
                  <SelectTrigger data-testid="extract-split-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SPLIT_MODES.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`extract-split-${m.id}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {splitMode !== "none" && (
                <div className="space-y-2">
                  <Label htmlFor="split-value">
                    {splitMode === "records" ? "Records per file" : "Max size per file (MB)"}
                  </Label>
                  <Input
                    id="split-value"
                    type="number"
                    min={splitMode === "size" ? 0.1 : 1}
                    step={splitMode === "size" ? 0.1 : 100}
                    value={splitValue}
                    onChange={(e) => setSplitValue(e.target.value)}
                    className="h-11"
                    data-testid="extract-split-value"
                  />
                </div>
              )}
            </div>

            {splitMode !== "none" && (
              <p className="-mt-1 text-xs text-muted-foreground">
                {format === "xlsx"
                  ? "Excel export puts each chunk on its own sheet — whole rows only, never split."
                  : "Each file holds whole rows only (no row is broken across files); files are delivered as a ZIP."}
              </p>
            )}

            {queryMode === "objects" && (
              <div className="space-y-2">
                <Label>Combine output</Label>
                <Select value={combine} onValueChange={setCombine}>
                  <SelectTrigger data-testid="extract-combine-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMBINE_MODES.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`extract-combine-${m.id}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {combine === "separate"
                    ? "Each object is exported to its own file (multiple files delivered as a ZIP)."
                    : combine === "stacked"
                    ? "All selected objects are stacked into one file with an “Object” column identifying each row."
                    : "Related objects are joined into one wide table (e.g. each Opportunity row enriched with its Account fields). Falls back to stacked when no relationship is found."}
                </p>
              </div>
            )}

            <div className="space-y-2 sm:max-w-[50%] sm:pr-2">
              <Label htmlFor="row-limit">{queryMode === "soql" ? "Max rows" : "Max rows per object"}</Label>
              <Input
                id="row-limit"
                type="number"
                min={0}
                max={20000000}
                step={1000}
                value={rowLimit}
                onChange={(e) => setRowLimit(e.target.value)}
                className="h-11"
                data-testid="extract-row-limit"
              />
              {system === "salesforce" && queryMode === "objects" && format === "csv" && combine === "separate" ? (
                <p className="text-xs text-muted-foreground">
                  Enter <strong>0 for all rows (no limit)</strong> — millions supported via Salesforce Bulk API, streamed and auto-split into CSV files (ZIP). Runs in the background.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Up to 50,000 rows. For unlimited/large exports, pick <strong>Salesforce · Object picker · CSV · Separate files</strong>.
                </p>
              )}
            </div>

            {queryMode === "soql" ? (
              <div className="space-y-2" data-testid="extract-soql-block">
                <div className="flex items-center justify-between">
                  <Label htmlFor="soql-input">SOQL query</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => setSaveSoqlOpen(true)}
                    disabled={!soql.trim().toLowerCase().startsWith("select")}
                    data-testid="soql-save-open"
                  >
                    <Bookmark className="h-3.5 w-3.5" /> Save query
                  </Button>
                </div>
                {savedSoql.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="soql-saved">
                    {savedSoql.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 py-1 pl-3 pr-1 text-xs"
                        data-testid={`soql-saved-${t.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => setSoql(t.query)}
                          className="font-medium text-primary hover:underline"
                          data-testid={`soql-saved-load-${t.id}`}
                          title={t.query}
                        >
                          {t.name}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSoqlTemplate(t.id)}
                          className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                          aria-label={`Delete saved query ${t.name}`}
                          data-testid={`soql-saved-delete-${t.id}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Starter queries</p>
                <div className="flex flex-wrap gap-1.5" data-testid="soql-templates">
                  {SOQL_TEMPLATES.map((t) => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => setSoql(t.query)}
                      className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                      data-testid={`soql-template-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      title={t.query}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <Textarea
                  id="soql-input"
                  value={soql}
                  onChange={(e) => setSoql(e.target.value)}
                  placeholder="SELECT Id, Name, Amount, Account.Name FROM Opportunity WHERE StageName = 'Closed Won'"
                  className="min-h-[140px] font-mono text-sm"
                  data-testid="extract-soql-input"
                />
                <p className="text-xs text-muted-foreground">
                  Pick a starter query above or paste your own. Parent relationships (e.g. <code>Account.Name</code>) become dotted
                  columns; child subqueries are exported as JSON. Row limit above caps the result set.
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  Objects {objSource === "live" && <span className="text-xs font-normal text-primary">· live</span>}
                </Label>
                <span className="text-xs text-muted-foreground">
                  {selected.length}/{MAX_OBJECTS} selected
                </span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search objects…"
                  className="h-11 pl-9"
                  data-testid="extract-search"
                />
              </div>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-border bg-card" data-testid="extract-object-list">
                {loadingObjects ? (
                  <div className="flex h-24 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : filtered.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No objects found.</p>
                ) : (
                  filtered.map((o) => {
                    const on = selected.includes(o.name);
                    return (
                      <button
                        key={o.name}
                        onClick={() => toggle(o.name)}
                        className={`flex w-full items-center justify-between gap-3 border-b border-border/60 px-4 py-2.5 text-left text-sm transition-colors last:border-b-0 hover:bg-muted/50 ${on ? "bg-primary/5" : ""}`}
                        data-testid={`extract-obj-${o.name}`}
                      >
                        <span className="truncate">
                          <span className="font-medium">{o.label || o.name}</span>
                          {o.label && o.label !== o.name && <span className="ml-2 text-xs text-muted-foreground">{o.name}</span>}
                          {o.custom && <span className="ml-2 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">custom</span>}
                        </span>
                        <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                          {on ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3 text-muted-foreground" />}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {recs.length > 0 && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="extract-recommendations">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <Sparkles className="h-3.5 w-3.5" /> Recommended related objects
                </p>
                <div className="flex flex-wrap gap-2">
                  {recs.slice(0, 8).map((r) => (
                    <button
                      key={r.name}
                      onClick={() => addRec(r.name)}
                      className="rounded-full border border-primary/40 bg-background px-3 py-1 text-xs font-medium transition-colors hover:bg-primary hover:text-primary-foreground"
                      data-testid={`extract-rec-${r.name}`}
                    >
                      + {r.name} <span className="opacity-60">({r.reason})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Right: selection + run */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                {queryMode === "soql" ? "SOQL export" : "Selected objects"}
              </p>
              {queryMode === "soql" ? (
                <div className="space-y-2" data-testid="extract-soql-summary">
                  <p className="text-sm text-muted-foreground">
                    Runs your custom SOQL query against the connected Salesforce org and exports the result set.
                  </p>
                  <div className="rounded-lg border border-border bg-background px-3 py-2">
                    <p className="truncate font-mono text-xs text-muted-foreground">
                      {soql.trim() ? soql.trim().slice(0, 80) + (soql.trim().length > 80 ? "…" : "") : "No query entered yet"}
                    </p>
                  </div>
                </div>
              ) : selected.length === 0 ? (
                <p className="text-sm text-muted-foreground">Pick objects from the list to extract.</p>
              ) : (
                <div className="space-y-2" data-testid="extract-selected">
                  {selected.map((s) => {
                    const cols = fieldsByObject[s];
                    return (
                      <div
                        key={s}
                        className="flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{s}</p>
                          <p className="text-xs text-muted-foreground">
                            {cols && cols.length ? `${cols.length} column${cols.length === 1 ? "" : "s"}` : "all columns"}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            onClick={() => openFieldPicker(s)}
                            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            title="Choose columns"
                            data-testid={`extract-fields-${s}`}
                          >
                            <SlidersHorizontal className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => toggle(s)}
                            className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            aria-label={`Remove ${s}`}
                            data-testid={`extract-remove-${s}`}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Button
                onClick={run}
                disabled={running || restricted || (queryMode === "soql" ? !soql.trim().toLowerCase().startsWith("select") : !selected.length)}
                className="mt-5 w-full gap-2 font-semibold"
                data-testid="extract-run"
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <DownloadCloud className="h-4 w-4" />}
                {running
                  ? runStatus || "Extracting…"
                  : queryMode === "soql"
                  ? "Run SOQL export"
                  : `Extract ${selected.length || ""} object${selected.length === 1 ? "" : "s"}`}
              </Button>
              {restricted && <p className="mt-2 text-xs text-muted-foreground">Read-only for now — continue with a plan to extract.</p>}
              <Button
                variant="outline"
                onClick={() => setSavePresetOpen(true)}
                disabled={restricted || queryMode === "soql" || !selected.length}
                className="mt-2 w-full gap-2"
                data-testid="preset-save-open"
              >
                <Bookmark className="h-4 w-4" /> Save as preset
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled exports manager */}
      {scheduled.length > 0 && (
        <>
          <h2 className="mb-3 mt-10 font-display text-lg font-bold">Scheduled exports</h2>
          <div className="overflow-hidden rounded-xl border border-border" data-testid="schedules-panel">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Preset</th>
                  <th className="px-4 py-3">System</th>
                  <th className="px-4 py-3">Frequency</th>
                  <th className="px-4 py-3">Next run</th>
                  <th className="px-4 py-3">Last status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {scheduled.map((p) => {
                  const st = p.last_run_status || "—";
                  const stColor = st.startsWith("error")
                    ? "text-destructive"
                    : st.startsWith("delivered")
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-muted-foreground";
                  return (
                    <tr key={p.id} className="border-t border-border" data-testid={`schedule-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">{SYSTEM_LABELS[p.system] || p.system_name}</td>
                      <td className="px-4 py-3 capitalize text-muted-foreground">{p.schedule}</td>
                      <td className="px-4 py-3 text-muted-foreground">{p.next_run_at ? fmtDate(p.next_run_at) : "—"}</td>
                      <td className={`px-4 py-3 ${stColor}`}>
                        {st}
                        {p.last_run_at && <span className="ml-1 text-xs text-muted-foreground/70">· {fmtDate(p.last_run_at)}</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => runNow(p)}
                          data-testid={`schedule-run-now-${p.id}`}
                        >
                          <PlayCircle className="h-4 w-4" /> Run now
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* History */}
      <h2 className="mb-3 mt-10 font-display text-lg font-bold">Recent extractions</h2>
      <div className="overflow-hidden rounded-xl border border-border" data-testid="extract-history">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">System</th>
              <th className="px-4 py-3">Objects</th>
              <th className="px-4 py-3">Rows</th>
              <th className="px-4 py-3">Format</th>
              <th className="px-4 py-3">Files</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No extractions yet.</td>
              </tr>
            ) : (
              history.map((h) => (
                <tr key={h.id} className="border-t border-border" data-testid={`extract-history-${h.id}`}>
                  <td className="px-4 py-3">{fmtDate(h.created_at)}</td>
                  <td className="px-4 py-3">{SYSTEM_LABELS[h.system] || h.system_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{h.objects.length} · {h.objects.slice(0, 3).join(", ")}{h.objects.length > 3 ? "…" : ""}</td>
                  <td className="px-4 py-3">{(h.total_rows || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 uppercase text-muted-foreground">{h.format}</td>
                  <td className="px-4 py-3 text-muted-foreground">{h.file_count || 1}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtBytes(h.total_size_bytes)}</td>
                  <td className="px-4 py-3 text-right">
                    {h.status === "error" ? (() => {
                      const raw = h.error || "";
                      const isAuth = h.reconnect_required || /invalid_grant|sf_refresh_failed|expired access|expired refresh|revoked/i.test(raw);
                      const msg = isAuth
                        ? "Your Salesforce connection has expired. Reconnect the org from the Connections page, then run the extraction again."
                        : (raw || "Extraction failed.");
                      return (
                        <div className="flex flex-col items-end gap-1" data-testid={`extract-error-${h.id}`}>
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400">
                            <AlertTriangle className="h-3.5 w-3.5" /> Failed
                          </span>
                          <span className="max-w-[280px] text-right text-xs text-muted-foreground">
                            {msg}
                          </span>
                          {isAuth && (
                            <Link
                              to="/dashboard/connections"
                              className="text-xs font-semibold text-primary hover:underline"
                              data-testid={`extract-reconnect-${h.id}`}
                            >
                              Reconnect Salesforce →
                            </Link>
                          )}
                        </div>
                      );
                    })() : h.status === "running" ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => downloadFile(h.id, h.filename)}
                        disabled={downloading === h.id}
                        data-testid={`extract-download-${h.id}`}
                      >
                        {downloading === h.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                        Download
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Field picker dialog */}
      <Dialog open={!!fieldDialogObj} onOpenChange={(o) => !o && setFieldDialogObj(null)}>
        <DialogContent className="max-w-lg" data-testid="field-picker-dialog">
          <DialogHeader>
            <DialogTitle>Columns for {fieldDialogObj}</DialogTitle>
            <DialogDescription>
              Choose which fields to include in the export. Leave all selected to export every column.
            </DialogDescription>
          </DialogHeader>

          {loadingFields ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={fieldSearch}
                    onChange={(e) => setFieldSearch(e.target.value)}
                    placeholder="Search fields…"
                    className="h-10 pl-9"
                    data-testid="field-search"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWorkingFields(availableFields.map((f) => f.name))}
                  data-testid="field-select-all"
                >
                  All
                </Button>
                <Button variant="outline" size="sm" onClick={() => setWorkingFields([])} data-testid="field-clear">
                  None
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {workingFields.length} of {availableFields.length} selected
              </p>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-border" data-testid="field-list">
                {filteredFields.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No fields found.</p>
                ) : (
                  filteredFields.map((f) => (
                    <label
                      key={f.name}
                      className="flex cursor-pointer items-center gap-3 border-b border-border/60 px-4 py-2 text-sm last:border-b-0 hover:bg-muted/50"
                      data-testid={`field-item-${f.name}`}
                    >
                      <Checkbox
                        checked={workingFields.includes(f.name)}
                        onCheckedChange={() => toggleWorkingField(f.name)}
                        data-testid={`field-check-${f.name}`}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{f.label || f.name}</span>
                        {f.label && f.label !== f.name && <span className="ml-2 text-xs text-muted-foreground">{f.name}</span>}
                      </span>
                      {f.type && <span className="shrink-0 text-[10px] uppercase text-muted-foreground">{f.type}</span>}
                    </label>
                  ))
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setFieldDialogObj(null)} data-testid="field-cancel">
              Cancel
            </Button>
            <Button onClick={applyFields} disabled={loadingFields || workingFields.length === 0} data-testid="field-apply">
              Apply columns
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save preset dialog */}
      <Dialog open={savePresetOpen} onOpenChange={setSavePresetOpen}>
        <DialogContent className="max-w-md" data-testid="save-preset-dialog">
          <DialogHeader>
            <DialogTitle>Save as preset</DialogTitle>
            <DialogDescription>
              Save this system, {selected.length} object{selected.length === 1 ? "" : "s"}, chosen columns, format &amp;
              row-limit as a one-click reusable preset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="preset-name">Preset name</Label>
            <Input
              id="preset-name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="e.g. Weekly Salesforce accounts"
              className="h-11"
              data-testid="preset-name-input"
              onKeyDown={(e) => e.key === "Enter" && savePreset()}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSavePresetOpen(false)} data-testid="preset-save-cancel">
              Cancel
            </Button>
            <Button onClick={savePreset} disabled={savingPreset || !presetName.trim()} data-testid="preset-save-confirm">
              {savingPreset ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save SOQL query dialog */}
      <Dialog open={saveSoqlOpen} onOpenChange={setSaveSoqlOpen}>
        <DialogContent className="max-w-md" data-testid="save-soql-dialog">
          <DialogHeader>
            <DialogTitle>Save SOQL query</DialogTitle>
            <DialogDescription>
              Save this query as a reusable named template you can load with one click later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="soql-name">Query name</Label>
            <Input
              id="soql-name"
              value={soqlName}
              onChange={(e) => setSoqlName(e.target.value)}
              placeholder="e.g. High-value open opps"
              className="h-11"
              data-testid="soql-name-input"
              onKeyDown={(e) => e.key === "Enter" && saveSoqlTemplate()}
            />
            <p className="truncate rounded-md border border-border bg-muted/40 px-2.5 py-1.5 font-mono text-[11px] text-muted-foreground">
              {soql.trim().slice(0, 90) || "No query"}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveSoqlOpen(false)} data-testid="soql-save-cancel">
              Cancel
            </Button>
            <Button onClick={saveSoqlTemplate} disabled={savingSoql || !soqlName.trim()} data-testid="soql-save-confirm">
              {savingSoql ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save query"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={!!scheduleTarget} onOpenChange={(o) => !o && setScheduleTarget(null)}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule “{scheduleTarget?.name}”</DialogTitle>
            <DialogDescription>
              Run this export automatically and email a secure download link to <strong>{user?.email}</strong>. The link
              is valid for 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Frequency</Label>
            <Select value={scheduleFreq} onValueChange={setScheduleFreq}>
              <SelectTrigger data-testid="schedule-freq-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily" data-testid="schedule-freq-daily">Every day</SelectItem>
                <SelectItem value="weekly" data-testid="schedule-freq-weekly">Every week</SelectItem>
              </SelectContent>
            </Select>
            {scheduleTarget?.schedule_enabled && (
              <p className="text-xs text-muted-foreground">
                Currently scheduled <strong>{scheduleTarget.schedule}</strong>
                {scheduleTarget.last_run_status ? ` · last run: ${scheduleTarget.last_run_status}` : ""}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            {scheduleTarget?.schedule_enabled ? (
              <Button
                variant="outline"
                onClick={() => saveSchedule("none")}
                disabled={savingSchedule}
                className="text-destructive hover:text-destructive"
                data-testid="schedule-turn-off"
              >
                Turn off schedule
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setScheduleTarget(null)} data-testid="schedule-cancel">
                Cancel
              </Button>
              <Button onClick={() => saveSchedule(scheduleFreq)} disabled={savingSchedule} data-testid="schedule-save">
                {savingSchedule ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save schedule"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
